import { computed, effect, EffectRef, inject, Injectable, signal, Signal, WritableSignal } from "@angular/core";
import { MenuModes } from "@models/enums/components/Menu/MenuModes.enum";
import { MenuTypes } from "@models/enums/components/Menu/MenuTypes.enum";
import { AppNodes } from "@models/enums/dvm/DvmNodes.enum";
import { GroupAssignedAmenity } from "@models/interfaces/amenities/AmenityGroupAssigned.interface";
import { Menu, MenuConfiguration, MenuContent, MenuContentItem, MenuKeys } from "@models/interfaces/components/Menu.interface";
import { Configuration } from "@models/interfaces/configuration/index.interface";
import { Filter } from "@models/types/app/Filter.type";
import { AmenitiesService } from "@services/amenities.service";
import { DvmService } from "@services/dvm.service";
import { FilterService } from "@services/filter.service";
import { UiService } from "@services/ui.service";
import { first, firstValueFrom, Subscription } from "rxjs";
import { BottomSheetService } from "./bottom-sheet.service";
import { MenuContainerComponent } from "@components/minisite/topbar/menu/menu-container/menu-container.component";
import { LayoutService } from "@services/layout.service";
import { Location } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { MinimapService } from "./minimap.service";

@Injectable()
export class MenuService {

    // SERVICES
    private readonly config:        Signal<Configuration['Ui']>            = inject(UiService).uiConfig;
    private readonly ams:           AmenitiesService                       = inject(AmenitiesService);
    private readonly filters:       FilterService                          = inject(FilterService);
    private readonly dvm:           DvmService                             = inject(DvmService);
    private readonly layout:        LayoutService                          = inject(LayoutService);
    private readonly bottomSheet:   BottomSheetService                     = inject(BottomSheetService);
    private readonly location:      Location                               = inject(Location);
    private readonly route:         ActivatedRoute                         = inject(ActivatedRoute);
    private readonly minimap:       MinimapService                         = inject(MinimapService);
    
    // CONFIGURATION
    private readonly menuConfig:    Signal<Configuration['Ui']['Menus']>   = computed((): MenuConfiguration => this.config().Menus);

    // STATE
    public  readonly menus:         Map<MenuKeys, Menu>                    = new Map<MenuKeys, Menu>();
    private readonly selectedKey:   WritableSignal<MenuKeys|null>          = signal<MenuKeys|null>(null);
    public  readonly itemSelected:  WritableSignal<MenuContentItem | null> = signal<MenuContentItem|null>(null);
    private readonly onLoadSuccess: Subscription                           = this.dvm.viewer.getObservable('load_success').subscribe((): void => this.initMenuTags());

    // GETTERS
    public get menuSelected():      Menu | undefined {

        return this.selectedKey() ? this.menus.get(this.selectedKey()!) : undefined;
        
    }
    
    // EFFECTS
    private readonly configChange:  EffectRef                              = effect( async (): Promise<void> => {
        
        // Clear existing menus before setting new ones
        this.menus.clear();

        const hasMenuContent: boolean = Object.values(this.menuConfig().menus).some(menu => menu?.content.length);

        if (hasMenuContent) await this.setMenus();

    });

    // COMPONENT STATE
    public readonly showContainer:  WritableSignal<boolean|null>           = signal(null);
    public readonly isAnimating:    WritableSignal<boolean>                = signal(false);
    public readonly animation:      number                                 = 400;

   
    // PUBLIC METHODS
    
    /**
     * Método principal para mostrar u ocultar el contenedor de menú además de cambiar el menú seleccionado.
     * Principalmente el método se encarga de cambiar el menú seleccionado y mostrar u ocultar el contenedor de menú. Para ello, 
     * comprueba si no se está ejecutando una animación para no superponerlas. 
     * 
     * 1. Si no hay un menú seleccionado, se selecciona el menú y se muestra el contenedor de menú.
     * 2. Si ya hay un menú seleccionado, se comprueba que no sea el mismo menú el que se quiere seleccionar. En caso de serlo, se oculta el contenedor de menú.
     *    Si es diferente, se cierra el contenedor y se espera lo que dura la animación para hacer el cambio de menú y reabrir el contenedor.
     * @param { number | string } id 
     * @returns 
     */
    public toggleMenuContainer(key: MenuKeys): void {

        // Si esta animando no hace nada
        if(this.isAnimating()) return;

        // Si no hay un menú seleccionado, se selecciona el menú y se muestra el contenedor de menú.
        if (this.selectedKey() === null) {

            this.setMenuSelected(key);
            this.showContainer.set(true);
            
            if(this.layout.isMobile()) {
                this.bottomSheet.open(MenuContainerComponent).close.subscribe((): void => this.toggleMenuContainer(this.selectedKey()!));
            }

        }      
        // Si el menú seleccionado no es el mismo que ya esta seleccionado, se cierra el contenedor de menú, se espera a que termine la animación y se cambia el menú seleccionado.
        else if (this.selectedKey() !== key) {
            
            this.showContainer.set(false);
            
            setTimeout((): void => {

                this.setMenuSelected(key);           // Establece el menú seleccionado
                this.showContainer.set(true);   // Muestra el contenedor de menú

            }, this.animation);
            
        }
        // Si se selecciona el mismo menú que ya estaba seleccionado, actua de toggler ocultando el contenedor de menú y cambiando el menú y el menu item seleccionado a null.
        else {
    
            this.showContainer.set(false)
            // this.setMenuItem(null); // Cambia el menu item seleccionado a null    
            setTimeout((): void => this.selectedKey.set(null), this.animation);

        }
    }

    /**
     * Método que cierra el contenedor de menú si hay un menú seleccionado.
     * 
     * Este método se utiliza para cerrar el contenedor de menú cuando se selecciona un elemento del menú o se hace hover sobre él.
     * Si no hay un menú seleccionado, no hace nada.
     */
    public closeMenuContainer(): void {

        if(this.selectedKey() === null) return;

        this.toggleMenuContainer(this.selectedKey()!);
    }

    /**
     * Método que establece el elemento del menú seleccionado.
     * 
     * Este método se utiliza para seleccionar un elemento del menú y actualizar la disponibilidad de los asientos relacionados.
     * Si el elemento seleccionado es el mismo que el actual, se deselecciona.
     * 
     * @param menuItem - Elemento del menú a seleccionar.
     */
    public setMenuItem(menuItem: MenuContent | null): void {

        // Si el menu es AMS, seteamos el amenity seleccionado en el servicio AMS.
        if(menuItem?.amsId && this.ams.amsMap.get(menuItem.amsId)) {

            this.ams.setAmenityFromMenu(this.ams.amsMap.get(menuItem.amsId) as GroupAssignedAmenity);

        }
        // Si no es AMS, pero hay un amenity seleccionado, lo reseteamos.
        else if(this.ams.selectedAmenities().length) {
            
            this.ams.resetSelectedAmenities();

        }
        
        // Instanciamos menu data como MenuContentItem ( o null si no hay menuItem )
        const menu: MenuContentItem | null = menuItem ? menuItem.data : null;

        if(menu){

            // Si el menu seleccionado es el mismo que el actual, lo deseleccionamos, si no, establecemos el nuevo menú seleccionado.
            this.itemSelected.set(this.itemSelected()?.id === menu.id ? null : menu);

        }
        else{

            // Si el menu es null, deseleccionamos el item seleccionado.
            this.itemSelected.set(null);

        }
        
        // Deseleccionamos cualquier selección previa
        this.dvm.viewer.unselectAll()

        // Limpiamos los filtros
        this.filters.clearFilters();

        // Si hay elemento seleccionado
        if ( this.itemSelected() ){

            // Seteamos los filtros
            for(const [key, value] of Object.entries(this.itemSelected()!.relatedSeats || {}))
            this.filters.setFilters(key as keyof Filter, value);

            // Set availability for each node type on the map.
            // For each type, only the relatedSeats.section IDs that belong to that type remain available.
            // This supports maps with non-standard types (area, areaprice, etc.) in addition to section.
            const sectionIds: string[] = this.filters.filter.section;
            this.dvm.viewer.getTypesList().forEach((type: string) => {
                if (type === AppNodes.Ga) return;
                const idsOfType: string[] = this.dvm.viewer.getNodesByType(type).map(n => n.id!);
                const available: string[] = sectionIds.filter(id => idsOfType.includes(id));
                this.dvm.viewer.setAvailability(type, available);
            });

            // Forzamos la selección de las secciones
            this.dvm.viewer.select(this.filters.filter.section);

        }
        // Si estamos deseleccionando
        else {
            
            this.dvm.setAvailability();

        }
        
        // Si estamos en mobile y el bottom sheet está abierto, lo cerramos.
        if(this.layout.isMobile() && this.bottomSheet.state() !== 'closed') this.bottomSheet.close();

        // Hacemos que el viewer vaya a la selección o regrese su vista original.
        // Filtramos los IDs de sección que existan en el mapa para evitar errores de "Invalid destination".
        const sectionIds:        string[] = this.itemSelected()?.relatedSeats?.section ?? [];
        const validDestination:  string[] = sectionIds.filter(id => !!this.dvm.viewer.getNodeById(id));
        this.dvm.viewer.goTo(
            validDestination.length ? validDestination : this.dvm.viewer.limits.bbox_scene
        );

        this.updateMenuParams();  // Update params when selecting menu item

    }

    /**
     * Método que abre una vista 3D del DVM.
     * 
     * Este método se utiliza para cargar una vista 3D del DVM cuando se selecciona un elemento del menú que tiene la propiedad is3dView a true.
     * @param viewId - ID de la vista 3D a cargar.
     **/
    public openMenu3dView(viewId: string): void {        

        this.dvm.load3dView(viewId).subscribe(() => {
          this.clearCurrentSelection();
          // Ensure this signal change runs after any other updates to avoid batching
          queueMicrotask(() => this.minimap.setVisibility(false))
        });

    }

    /**
     * Método que limpia la selección actual del menú, filtros y amenities.
     * 
     **/
    public clearCurrentSelection(): void {

        // Limpiamos el item seleccionado
        if(this.itemSelected()) this.itemSelected.set(null);

        // Si es mobil y el bottom sheet está abierto, lo cerramos.
        if(this.layout.isMobile() && this.bottomSheet.state() !== 'closed') this.bottomSheet.close();

        // Limpiamos seleccion DVM
        this.dvm.viewer.unselectAll()
        
        // Limpiamos filtros
        this.filters.clearFilters();

        // Reseteamos amenities seleccionados
        this.ams.resetSelectedAmenities();
        
    }

    /**
     * Método que activa el hover sobre un elemento del menú.
     * 
     * Este método se utiliza para activar el hover sobre un elemento del menú y resaltar los asientos relacionados.
     * Si no se pasa un elemento del menú, se desactiva el hover.
     * 
     * @param menuItem - Elemento del menú sobre el que hacer hover.
     */
    public hoverMenuItem(menuItem?: MenuContentItem): void {

        if(!menuItem || !menuItem.relatedSeats?.section?.length) return; 

        this.dvm.viewer.hover(menuItem.relatedSeats?.section)

    }

    /**
     * Método que desactiva el hover sobre el menú.
     * 
     * Este método se utiliza para desactivar el hover sobre el menú y eliminar los asientos resaltados.
     * 
     */
    public leaveMenuItem(): void {

        this.dvm.viewer.hover([]);

    }

    /**
     * Método que comprueba si el menú seleccionado tiene el elemento seleccionado.
     * 
     * Este método se utiliza para comprobar si el menú seleccionado tiene el elemento seleccionado.
     * Si no hay un menú seleccionado o no hay un elemento seleccionado, devuelve false.
     * @param key - Clave del menú a comprobar.
     * @returns true si el menú seleccionado tiene el elemento seleccionado, false en caso contrario.
     **/
    public hasMenuSelectedItem(key: MenuKeys): boolean {

        const menu:     Menu | undefined        = this.menus.get(key),
              selected: MenuContentItem | null  = this.itemSelected();
    
        if (!menu || !selected) return false;

        return menu.content.some(item => item.data?.id === selected.id);

    }

    /**
     * Método que selecciona un elemento del menú.
     * 
     * Si el elemento del menú tiene la propiedad is3dView a true y tiene un viewId, se carga la vista 3D correspondiente.
     * Si no, se establece el elemento del menú como el elemento seleccionado.
     * @param item - Elemento del menú a seleccionar.
     **/
    public selectItem(item: MenuContent | null): void {

        if (item?.data.is3dView && item.data.viewId) {
            this.openMenu3dView(item.data.viewId);
        } else {
            this.setMenuItem(item);
        }

    }

    /**
     * Método que inicializa los tags y los grupos de los nodos del mapa segun el contenido de los menús
     */
    public initMenuTags(): void {

        this.menus.forEach((menu: Menu): void => this.setTagsByMenu(menu));

    }

    // PRIVATE METHODS

    /**
     * Método que establece el valor de la propiedad selectedMenu.
     * @param menuId 
     */
    private setMenuSelected(menuKey: MenuKeys): void {
        
        if(this.selectedKey() !== menuKey) this.selectedKey.set(menuKey);

    }

    /**
     * Método que actualiza los parámetros del menú en la URL.
     * @returns 
     */
    private updateMenuParams(): void {

        const currentParams: URLSearchParams        = new URLSearchParams(window.location.search),
              selectedMenu:  MenuKeys | null        = this.selectedKey(),
              selectedItem:  MenuContentItem | null = this.itemSelected();

        // check initially — if nothing selected, clear both and return
        if (!selectedMenu || !selectedItem) {
            currentParams.delete('menu_id');
            currentParams.delete('menu_item_id');
            this.location.replaceState(window.location.pathname, currentParams.toString());
            return;
        }

        const menu: Menu | undefined = this.menus.get(selectedMenu);
      
        if (!menu) return;

        // Try to match by id
        const matchedItem: MenuContent | undefined = menu.content.find(item => item.data.id === selectedItem.id);

        // Only add params if valid combo (type matches or found in menu)
        if (selectedItem.type === selectedMenu || matchedItem) {

            currentParams.set('menu_id', selectedMenu);
            currentParams.set('menu_item_id', String(selectedItem.id));

        } else {

            currentParams.delete('menu_id');
            currentParams.delete('menu_item_id');

        }

        this.location.replaceState(window.location.pathname, currentParams.toString());

    }

    /**
     * Método que selecciona un menú y carga un elemento del menú una vez que el DVM ha terminado de cargar.
     * Este método se usa cuando venimos de un copy link con parámetros de menú en la URL.
     * 
     * @param menuId - ID del menú a seleccionar.
     * @param menuItemId - ID del elemento del menú a seleccionar.
     */
    private selectMenuAndLoadItem(menuId: string, menuItemId: string): void {
        
        this.dvm.viewer.getObservable('load_success')
        .pipe(first()) // unsubscribe automatically after first event
        .subscribe((): void => {
            
            const menu:         Menu | undefined    = this.menus.get(menuId as MenuKeys),
                  selectedItem: MenuContent | null  = menu?.content.find(item => String(item.data.id) === menuItemId) ?? null,
                  duration:     number              = this.layout.isMobile() ? this.animation + 100 : this.animation;

            
            // Open the menu only if not on mobile
            if (!this.layout.isMobile() && menuId) {
                this.toggleMenuContainer(menuId as MenuKeys);
            } 
            
            // Select the item after the animation duration
            setTimeout(() => {
                if (selectedItem) {
                    this.selectItem(selectedItem);
                }
            }, duration);

         
        });
    }

    //#region MENU SETTER
    
    /**
     * Método que establece los menús en el servicio.
     * 
     * Recorre la configuración de menús y crea un array de promsesas para procesar cada menú de manera asíncrona.
     * Esto es porque en el sistema de menús pueden haber menús que vengan informados de manera manual y otros que vengan por api.
     * Para estandarizar las interfaces, tanto del menu normal como el de ams, se procesan los datos de configuración de manera asíncrona, 
     * así se garantiza que todos los menús esten disponibles a la vez y que el mapa de menús (this.menus) no se modifique 
     * en diferentes momentos, ya que es donde los componentes consumen los menus y provocarian repintados en el DOM innecesarios.
     * 
     * Nota: si el elemento no es asincrono (viene dado de manera manual), aunque sea una promesa asincrona, se resolverá inmediatamente. 
     * 
     * Para cada menú se realiza un proceso que envuelve en una promesa los siguientes pasos:
     * 1. Se comprueba si el menú contiene contenido de tipo AMS. 
     * 2. Si tiene, se crea un menú especifico para AMS, si no se añade al mapa de menús directamente.
     * 3. Como el menu AMS proviene de una api, debemos esperar a que se resuelva.
     * 4. Una vez resuelto, se estandariza el contenido a una interfaz común para todos los menús. 
     * 5. Finalmente, se añade el menú al mapa de menús (this.menus).
     *
     * Una vez que todos los menús se han procesado y guardado en un array de promesas, se espera a que todas las promesas se resuelvan.
     * 
     */
    private async setMenus(): Promise<void> {

        const menus:        Record<MenuKeys,Menu|undefined> = this.menuConfig().menus,
              menuEntries:  [string, Menu | undefined][]    = Object.entries(menus),
              menuPromises: Promise<void>[]                 = menuEntries.map(async ([key, menu]): Promise<void> => this.createMenu(key as MenuKeys, menu))
              
        await Promise
            .allSettled(menuPromises)
            .then((): void => {

                // Revisamos los query params despues de cargar los menus
                const menuId:     string | null = this.route.snapshot.queryParamMap.get('menu_id'),
                      menuItemId: string | null = this.route.snapshot.queryParamMap.get('menu_item_id');

                // Indicamos que el mapa de menús ya está cargado al subject
                this.ams.amsMapLoaded$.next(true);
                
                //if menu params are present, select menu and item on load
                if(menuId && menuItemId && this.menus.get(menuId as MenuKeys)) 
                this.selectMenuAndLoadItem(menuId, menuItemId);
                              
            })
    
    }

    /**
     * Método que crea un menú y lo añade al mapa de menús.
     * 
     * Comprueba si el menú tiene contenido de tipo AMS, si lo tiene, crea un menú específico para AMS.
     * Si no lo tiene, añade el menú directamente al mapa de menús.
     * 
     * @param key  - Clave del menú a crear.
     * @param menu - Menú a crear.
     * 
     **/
    private async createMenu(key:string, menu?: Menu): Promise<void> {
        
        if (!menu || (menu && !menu.content.length)) return;

        const hasAmsContent: boolean = menu.content.some((item) => item.amsId);

        if(hasAmsContent) await this.createAmsMenu(key, menu)      

        else this.menus.set(key as MenuKeys, menu);
        
    }

    /**
     * Método que crea un menú específico para AMS.
     * 
     * Como los menus pueden mezclar contenido de tipo AMS y contenido normal, separamos el contenido en dos arrays:
     *  1. amsContent: Contenido de tipo AMS.
     *  2. regularContent: Contenido normal.
     * 
     * En los menus de tipo AMS, el contenido es un id de la api de AMS, por lo que obtenemos los ids (amsIds) de cada item del contenido AMS
     * y hacemos una llamada a la api de AMS para obtener los datos de los amenities.
     * 
     * Una vez obtenidos los amenities, se itera sobre el contenido AMS y se sobreescribe el campo data de cada item por un objeto de tipo MenuContentItem.
     * De esta manera, se estandariza el contenido de los menús de tipo AMS con el contenido normal.
     * 
     * Finalmente, se une el contenido AMS con el contenido normal y se añade al mapa de menús.
     * 
     * @param {string} key 
     * @param { Menu } menu 
     */
    private async createAmsMenu(key: string, menu: Menu): Promise<void> {

        const   amsContent:      MenuContent[]                  = menu.content.filter((item) => item.amsId),                // Array de MenuContents que tengan un amsId.
                regularContent:  MenuContent[]                  = menu.content.filter((item) => !item.amsId),               // Array del resto de MenuContents que no tengan un amsId.
                amsIds:          string[]                       = amsContent.map((item) => item.amsId!.toString()),         // Array de ids de amsContent 
                hasData:         (item: MenuContent) => boolean = (item): boolean => Object.keys(item.data).length > 0,     // Helper para comprobar si un item tiene datos.
                amenities:       GroupAssignedAmenity[]         = await firstValueFrom(this.ams.searchAmenities(amsIds));   // Llamada a la api de AMS para obtener los datos de los amenities.

        // Si la llave es premium, pasamos los regular items a type premium
        if(key !== MenuTypes.regular) regularContent.forEach(item => item.data.type = key as MenuTypes);

        // Iteramos sobre los amenities obtenidos y sobreescribimos el objeto data del item que coincida con el id del amenity.
        amenities.forEach((amenity: GroupAssignedAmenity)=> {

            this.ams.amsMap.set(amenity.id, amenity); // Añadimos el amenity al mapa de amenities del servicio AMS.

            amsContent.find(item => item.amsId === amenity.id)!.data = this.createMenuContentItem(amenity);

        })

        // Comprobamos si el campo data de algun item de amsContent no tiene datos, si es asi lo eliminamos. 
        if(amsContent.some(item => !hasData(item))) { 

            // Si hay algun item que no cumple con hasData, ejecutamos splice y devolvemos false para que no se incluya en el array.
            amsContent.filter((item,index,arr) => hasData(item) || (arr.splice(index, 1), false));

        }

        // Ahora que tenemos el contenido AMS estandarizado, lo unimos con el contenido regular y reemplazamos el contenido del menú.
        // Join regularContent with amsContent but preserving original order
        menu.content = menu.content
          .map(item => {
            if (item.amsId) {
                return amsContent.find(amsItem => amsItem.amsId === item.amsId);
            } else {
                return regularContent.find(regItem => regItem === item);
            }
          })
          .filter(item => item != null) as MenuContent[];

        // Finalmente, añadimos el menú al mapa de menús.
        this.menus.set(key as MenuKeys, menu);
    }

    /**
     * Método que crea un objeto de tipo MenuContentItem a partir de un objeto de tipo GroupAssignedAmenity.
     * 
     * @param amenity - Objeto de tipo GroupAssignedAmenity.
     * @returns Un objeto de tipo MenuContentItem.
     */
    public createMenuContentItem(amenity: GroupAssignedAmenity): MenuContentItem {
        
        const itemContent: MenuContentItem = {
            id:     amenity.id,
            title:  amenity.title,
            type:   MenuTypes.premium,
            mode:   MenuModes.filter,
        };

        Object.assign(itemContent, amenity);

        if (amenity.relatedSeats && amenity.relatedSeats.length) itemContent.relatedSeats = this.parseRelatedSeats(amenity.relatedSeats);
        
        return itemContent;

    }

    /**
     * Método que parsea las asientos relacionados de un string a un objeto de tipo MenuContentItem['relatedSeats'].
     * 
     * @param seats - String con los asientos relacionados.
     * @returns Un objeto de tipo MenuContentItem['relatedSeats'].
     */
    private parseRelatedSeats(seats: string): MenuContentItem['relatedSeats'] {
        
        const relatedSeats: MenuContentItem['relatedSeats'] = {
            section:   [],
            row:       [],
            seat:      [...seats.split(';')],
        };
        
        const sectionSet = new Set<string>(),
              rowSet     = new Set<string>();

        
        seats.split(';').forEach((id: string) => {

            sectionSet.add(id.split('-')[0]);
            rowSet.add(id.split('-').slice(0, -1).join('-'));

        });
        
        relatedSeats.section = Array.from(sectionSet);
        relatedSeats.row     = Array.from(rowSet);
        
        return relatedSeats;

    }

    /**
     * Metodo que establece los tags y grupos a los nodos del mapa. 
     * Este método recibe un menú y inicializa un objeto content que contiene el tipo y todos los nodos como array.
     * Finalmente, recorre el objeto content y establece los tags y grupos a los nodos del mapa.
     *
     * @param menu - Menú del que se quieren establecer los tags y grupos.
     */
    private setTagsByMenu(menu: Menu): void {
        
        const content: { type: MenuTypes; nodes: string[] }[] = menu.content.map(item => ({ 
            type:   item.data.type, 
            nodes:  Object.values(item.data.relatedSeats!).flat() || [] 
        }));

        content.forEach(item => ( 
            // this.dvm.viewer.setNodesTag(item.nodes, item.type),
            this.dvm.viewer.addNodesToGroup(item.nodes, item.type)
        ));

    } 

    //#endregion MENU SETTER       

    
}